import React, { useEffect, useState } from 'react';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Sparkles, TrendingUp, Users, MessageCircle, Crown, Shield, BadgeCheck,
  LogOut, Settings, UserPlus, Edit, Bookmark, Bell, Lock, User, 
  PlusCircle, Check, X, ChevronRight, Mail, Eye, EyeOff, Save
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    posts: user?.posts_count || 0,
    followers: user?.followers_count || 0,
    following: user?.following_count || 0,
  });
  
  // People You May Know
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  
  // Friend Requests
  const [friendRequests, setFriendRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  
  // Saved Posts
  const [savedPosts, setSavedPosts] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  
  // Edit Profile Modal
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    username: user?.username || '',
    bio: user?.bio || '',
    avatar: user?.avatar || ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Recent Messages
  const [recentMessages, setRecentMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  
  // Notifications count
  const [unreadCount, setUnreadCount] = useState(0);

  const isPremium = user?.subscription_tier !== 'basic';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch suggestions (people you may know)
      const suggestionsRes = await axios.get(`${API_URL}/users/suggestions`, { withCredentials: true });
      setSuggestions(suggestionsRes.data?.slice(0, 5) || []);
    } catch (e) {
      console.log('Suggestions not available');
    } finally {
      setLoadingSuggestions(false);
    }

    try {
      // Fetch friend requests
      const requestsRes = await axios.get(`${API_URL}/users/friend-requests`, { withCredentials: true });
      setFriendRequests(requestsRes.data || []);
    } catch (e) {
      console.log('Friend requests not available');
    } finally {
      setLoadingRequests(false);
    }

    try {
      // Fetch saved/bookmarked posts
      const savedRes = await axios.get(`${API_URL}/bookmarks/`, { withCredentials: true });
      setSavedPosts(savedRes.data?.slice(0, 3) || []);
    } catch (e) {
      console.log('Saved posts not available');
    } finally {
      setLoadingSaved(false);
    }

    try {
      // Fetch recent messages
      const messagesRes = await axios.get(`${API_URL}/messages/conversations`, { withCredentials: true });
      setRecentMessages(messagesRes.data?.slice(0, 3) || []);
    } catch (e) {
      console.log('Messages not available');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await axios.put(`${API_URL}/users/profile`, profileForm, { withCredentials: true });
      if (setUser) {
        setUser({ ...user, ...response.data });
      }
      toast.success('Profile updated successfully!');
      setShowEditProfile(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleFollowSuggestion = async (userId) => {
    try {
      await axios.post(`${API_URL}/users/follow/${userId}`, {}, { withCredentials: true });
      setSuggestions(suggestions.filter(s => s.user_id !== userId));
      toast.success('Follow request sent!');
    } catch (error) {
      toast.error('Failed to follow user');
    }
  };

  const handleAcceptRequest = async (requestId, userId) => {
    try {
      await axios.post(`${API_URL}/users/friend-requests/${requestId}/accept`, {}, { withCredentials: true });
      setFriendRequests(friendRequests.filter(r => r.request_id !== requestId));
      toast.success('Friend request accepted!');
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axios.post(`${API_URL}/users/friend-requests/${requestId}/reject`, {}, { withCredentials: true });
      setFriendRequests(friendRequests.filter(r => r.request_id !== requestId));
      toast.success('Request declined');
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        
        {/* Header with User Info and Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="p-4 sm:p-6 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {/* User Info */}
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16 sm:w-20 sm:h-20 border-4 border-white shadow-lg">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-2xl font-bold">
                    {user?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl sm:text-2xl font-heading font-black text-gray-900 dark:text-white">
                      {user?.name || 'User'}
                    </h1>
                    {user?.is_verified === true && (
                      <BadgeCheck className="w-6 h-6 text-blue-500" title="Verified" />
                    )}
                    {user?.is_admin === true && (
                      <Shield className="w-6 h-6 text-red-500" title="Admin" />
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">@{user?.username || 'username'}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 capitalize mt-1">
                    {user?.subscription_tier || 'Basic'} Plan
                  </p>
                </div>
              </div>

              {/* Quick Action Buttons */}
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <Button
                  onClick={() => setShowEditProfile(true)}
                  variant="outline"
                  className="rounded-full flex-1 sm:flex-none"
                  data-testid="edit-profile-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
                <Link to="/settings" className="flex-1 sm:flex-none">
                  <Button variant="outline" className="rounded-full w-full">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Button>
                </Link>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="rounded-full text-red-600 hover:bg-red-50 flex-1 sm:flex-none"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-3 sm:gap-6 mb-6"
        >
          <Card className="p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center hover:shadow-2xl transition-all">
            <p className="text-2xl sm:text-4xl font-heading font-black text-gray-900 dark:text-white">{stats.posts}</p>
            <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">Posts</p>
          </Card>
          <Card className="p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center hover:shadow-2xl transition-all">
            <p className="text-2xl sm:text-4xl font-heading font-black text-gray-900 dark:text-white">{stats.followers}</p>
            <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">Followers</p>
          </Card>
          <Card className="p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center hover:shadow-2xl transition-all">
            <p className="text-2xl sm:text-4xl font-heading font-black text-gray-900 dark:text-white">{stats.following}</p>
            <p className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">Following</p>
          </Card>
        </motion.div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Quick Actions */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            {/* Quick Links */}
            <Card className="p-4 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
              <h3 className="font-bold text-gray-900 dark:text-white mb-3">Quick Access</h3>
              <div className="space-y-2">
                <Link to="/feed" className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white">Feed</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>
                
                <Link to="/messages" className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-testid="messages-link">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900 dark:text-white">Messages</span>
                      <p className="text-xs text-gray-500">E2E Encrypted DMs</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>

                <Link to="/talkspace" className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-testid="talkspace-link">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900 dark:text-white">Talkspace</span>
                      <p className="text-xs text-gray-500">Live Chat & Video Rooms</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>
                
                <Link to="/profile" className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white">My Profile</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>
                
                <Link to="/settings" className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900 dark:text-white">Security</span>
                      <p className="text-xs text-gray-500">2FA, Password, Privacy</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>
              </div>
            </Card>

            {/* Account Actions */}
            <Card className="p-4 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
              <h3 className="font-bold text-gray-900 dark:text-white mb-3">Account</h3>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    // Clear session and go to register
                    handleLogout();
                    setTimeout(() => navigate('/register'), 500);
                  }}
                  className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  data-testid="create-account-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                      <PlusCircle className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white">Create Another Account</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
                
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600"
                  data-testid="logout-btn-2"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <LogOut className="w-5 h-5 text-red-600" />
                    </div>
                    <span className="font-semibold">Logout</span>
                  </div>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </Card>

            {/* Admin Panel Link */}
            {user?.is_admin === true && (
              <Link to="/admin">
                <Card className="p-4 rounded-2xl border-2 border-red-300 shadow-xl bg-gradient-to-br from-red-500 to-orange-500 text-white hover:shadow-2xl transition-all cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Shield className="w-8 h-8" />
                    <div>
                      <h3 className="font-bold">Admin Panel</h3>
                      <p className="text-sm text-white/80">Manage users & content</p>
                    </div>
                  </div>
                </Card>
              </Link>
            )}
          </motion.div>

          {/* Middle Column - Messages & Saved Posts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            {/* Recent Messages / Talkspace */}
            <Card className="p-4 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                  Talkspace
                </h3>
                <Link to="/messages" className="text-sm text-primary hover:underline">View All</Link>
              </div>
              
              {loadingMessages ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : recentMessages.length === 0 ? (
                <div className="text-center py-6">
                  <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No messages yet</p>
                  <Link to="/explore">
                    <Button size="sm" className="mt-3 rounded-full">Find People to Chat</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentMessages.map((conv, idx) => (
                    <Link
                      key={conv.conversation_id || idx}
                      to="/messages"
                      className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={conv.other_user?.avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-sm font-bold">
                          {conv.other_user?.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                          {conv.other_user?.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{conv.last_message}</p>
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </Card>

            {/* Saved Posts */}
            <Card className="p-4 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-yellow-500" />
                  Saved Posts
                </h3>
                <Link to="/bookmarks" className="text-sm text-primary hover:underline">View All</Link>
              </div>
              
              {loadingSaved ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : savedPosts.length === 0 ? (
                <div className="text-center py-6">
                  <Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No saved posts</p>
                  <p className="text-xs text-gray-400 mt-1">Bookmark posts to see them here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {savedPosts.map((post, idx) => (
                    <div
                      key={post.post_id || idx}
                      className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50"
                    >
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                        {post.content || 'No content'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">by @{post.user?.username}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </motion.div>

          {/* Right Column - People You May Know */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-4"
          >
            {/* Friend Requests */}
            <Card className="p-4 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Bell className="w-5 h-5 text-orange-500" />
                  Friend Requests
                </h3>
                {friendRequests.length > 0 && (
                  <span className="bg-orange-500 text-white text-xs rounded-full px-2 py-0.5">
                    {friendRequests.length}
                  </span>
                )}
              </div>
              
              {loadingRequests ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : friendRequests.length === 0 ? (
                <p className="text-center text-gray-500 text-sm py-4">No pending requests</p>
              ) : (
                <div className="space-y-3">
                  {friendRequests.map((req, idx) => (
                    <div key={req.request_id || idx} className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={req.from_user?.avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-sm font-bold">
                          {req.from_user?.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                          {req.from_user?.name}
                        </p>
                        <p className="text-xs text-gray-500">@{req.from_user?.username}</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleAcceptRequest(req.request_id, req.from_user?.user_id)}
                          className="p-1.5 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRejectRequest(req.request_id)}
                          className="p-1.5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* People You May Know */}
            <Card className="p-4 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  People You May Know
                </h3>
                <Link to="/explore" className="text-sm text-primary hover:underline">See More</Link>
              </div>
              
              {loadingSuggestions ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : suggestions.length === 0 ? (
                <div className="text-center py-4">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No suggestions right now</p>
                  <Link to="/explore">
                    <Button size="sm" className="mt-3 rounded-full">Explore Users</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {suggestions.map((person, idx) => (
                    <div key={person.user_id || idx} className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={person.avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-sm font-bold">
                          {person.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                            {person.name}
                          </p>
                          {person.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
                        </div>
                        <p className="text-xs text-gray-500">@{person.username}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleFollowSuggestion(person.user_id)}
                        className="rounded-full bg-primary hover:bg-primary-700 text-white text-xs px-3"
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        Follow
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Upgrade Card */}
            {!isPremium && (
              <Card className="p-4 rounded-2xl border border-white/20 shadow-xl bg-gradient-to-br from-secondary via-accent to-primary text-white">
                <div className="flex items-center gap-3 mb-3">
                  <Crown className="w-8 h-8" />
                  <h3 className="font-bold text-lg">Go Premium</h3>
                </div>
                <p className="text-sm text-white/90 mb-4">
                  Unlock AI features, analytics, and more!
                </p>
                <Link to="/pricing">
                  <Button className="w-full rounded-full bg-white text-primary font-bold hover:bg-gray-100">
                    View Plans
                  </Button>
                </Link>
              </Card>
            )}
          </motion.div>
        </div>

        {/* Footer with Legal Links */}
        <footer className="mt-12 pb-8 text-center" data-testid="dashboard-footer">
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <Link 
              to="/privacy" 
              className="hover:text-primary transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link 
              to="/terms" 
              className="hover:text-primary transition-colors"
              data-testid="footer-terms-link"
            >
              Terms of Service
            </Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link 
              to="/conduct" 
              className="hover:text-primary transition-colors"
              data-testid="footer-conduct-link"
            >
              Code of Conduct
            </Link>
          </div>
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            © {new Date().getFullYear()} Socyy. All rights reserved.
          </p>
        </footer>
      </div>

      {/* Edit Profile Modal */}
      <AnimatePresence>
        {showEditProfile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowEditProfile(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-md shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Profile</h2>
                <button
                  onClick={() => setShowEditProfile(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Name</label>
                  <Input
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="rounded-xl"
                    data-testid="edit-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Username</label>
                  <Input
                    value={profileForm.username}
                    onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                    className="rounded-xl"
                    data-testid="edit-username-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Bio</label>
                  <Textarea
                    value={profileForm.bio}
                    onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                    className="rounded-xl resize-none"
                    rows={3}
                    data-testid="edit-bio-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Avatar URL</label>
                  <Input
                    value={profileForm.avatar}
                    onChange={(e) => setProfileForm({ ...profileForm, avatar: e.target.value })}
                    className="rounded-xl"
                    placeholder="https://..."
                    data-testid="edit-avatar-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => setShowEditProfile(false)}
                    variant="outline"
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="flex-1 rounded-full bg-primary hover:bg-primary-700 text-white"
                    data-testid="save-profile-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingProfile ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FloatingDock user={user} />
    </div>
  );
};

export default Dashboard;
